import { Fragment, useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type TouchEvent } from "react";
import { createPortal } from "react-dom";

import { Icon } from "@/components/layout/Icon";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils/cn";
import { useImageFallback } from "@/lib/utils/useImageFallback";
import { useMountTransition } from "@/lib/utils/useMountTransition";
import type { Announcement } from "@/types/domain";

/** The fields the popup renders - a saved Announcement, or the admin form's
 * unsaved draft in preview. */
export type PopupContent = Pick<
  Announcement,
  "id" | "title" | "body" | "type" | "priority" | "image_url" | "starts_at" | "event_at" | "location" | "cta_text" | "cta_url" | "published_at"
>;

interface AnnouncementPopupProps {
  items: PopupContent[];
  open: boolean;
  /** Called with the slide that was showing when the user closed it. */
  onClose: (current: PopupContent) => void;
  /** Fires once per slide the first time it is actually on screen. */
  onSlideShown?: (item: PopupContent) => void;
  /** Called when a CTA is pressed; the popup closes itself afterwards. */
  onCta?: (item: PopupContent) => void;
  /** "inline" renders inside the parent (admin preview frames) instead of a
   * full-screen portal, without locking page scroll. */
  variant?: "overlay" | "inline";
}

const SWIPE_THRESHOLD_PX = 50;

/** "15 Oct 2026" from a full timestamp, in the viewer's timezone - the
 * shared formatDate expects a bare "YYYY-MM-DD" and would echo an RFC3339
 * value back verbatim. */
function formatDay(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase();
}

/**
 * Login popup for Events & Announcements. One container, one slide at a
 * time; carousel controls appear only when there is more than one item.
 *
 * Deliberately has NO viewport breakpoints inside the card: its width comes
 * from the wrapper alone, so the admin's mobile preview (a 375px frame on a
 * desktop screen) renders exactly what a phone renders.
 */
export function AnnouncementPopup({ items, open, onClose, onSlideShown, onCta, variant = "overlay" }: AnnouncementPopupProps) {
  const [index, setIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const touchStartX = useRef<number | null>(null);
  const titleId = useId();
  const { shouldRender, isVisible } = useMountTransition(open && items.length > 0, 200);

  const count = items.length;
  const safeIndex = Math.min(index, Math.max(count - 1, 0));
  const current = items[safeIndex];

  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(count - 1, 0));
  }, [count, index]);

  useEffect(() => {
    if (open && current) onSlideShown?.(current);
    // Only when the visible slide changes, not on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, current?.id]);

  const close = useCallback(() => {
    if (current) onClose(current);
  }, [current, onClose]);

  const go = useCallback(
    (delta: number) => setIndex((i) => Math.min(Math.max(i + delta, 0), Math.max(count - 1, 0))),
    [count],
  );

  // Escape closes, arrows navigate; page scroll is locked while the real
  // (overlay) popup is open.
  useEffect(() => {
    if (!open || variant !== "overlay") return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, variant, close, go]);

  // Move focus into the dialog when it opens, and back where it was after.
  useEffect(() => {
    if (!open || variant !== "overlay") return;
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previous?.focus?.();
  }, [open, variant]);

  // Focus trap: Tab / Shift+Tab cycle inside the dialog.
  const onDialogKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    go(dx < 0 ? 1 : -1);
  };

  if (!shouldRender || !current) return null;

  const isEvent = current.type === "event";
  const isHigh = current.priority === "high";

  const card = (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal={variant === "overlay" ? "true" : undefined}
      aria-labelledby={titleId}
      aria-roledescription={count > 1 ? "carousel" : undefined}
      data-testid="announcement-popup"
      data-kind={current.type}
      onKeyDown={onDialogKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={cn(
        "relative flex max-h-full w-full flex-col overflow-hidden rounded-xl bg-white shadow-xl",
        "transition-all duration-200 ease-out motion-reduce:transition-none",
        isVisible ? "scale-100 opacity-100" : "scale-[0.98] opacity-0",
        !isEvent && isHigh && "ring-2 ring-red-200",
      )}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={close}
        aria-label="Close"
        className="absolute right-3 top-3 z-10 rounded-lg bg-white/90 p-1.5 text-slate-500 shadow-sm transition-colors hover:bg-primary-50 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600"
      >
        <Icon name="x" className="h-5 w-5" />
      </button>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto" aria-live="polite">
        {isEvent ? (
          <EventSlide item={current} titleId={titleId} />
        ) : (
          <AnnouncementSlide item={current} titleId={titleId} />
        )}
      </div>

      {((current.cta_text && current.cta_url) || count > 1) && (
        <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4">
          {current.cta_text && current.cta_url && (
            <Button
              fullWidth
              size="lg"
              variant={!isEvent && isHigh ? "danger" : "primary"}
              onClick={() => onCta?.(current)}
              data-testid="announcement-popup-cta"
            >
              <span className="truncate">{current.cta_text}</span>
            </Button>
          )}
          {count > 1 && (
            <div className="flex items-center justify-between gap-2" data-testid="announcement-popup-nav">
              <button
                type="button"
                onClick={() => go(-1)}
                disabled={safeIndex === 0}
                aria-label="Previous"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon name="chevron-left" className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-1.5" aria-hidden>
                {items.map((item, i) => (
                  <span
                    key={item.id}
                    className={cn("h-1.5 rounded-full transition-all", i === safeIndex ? "w-5 bg-primary-600" : "w-1.5 bg-slate-300")}
                  />
                ))}
              </div>
              <span className="min-w-[3rem] text-center text-xs font-medium tabular-nums text-slate-500" data-testid="announcement-popup-counter">
                {safeIndex + 1} / {count}
              </span>
              <button
                type="button"
                onClick={() => go(1)}
                disabled={safeIndex === count - 1}
                aria-label="Next"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Icon name="chevron-right" className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (variant === "inline") {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/40 p-4">
        <div className="flex max-h-full w-full max-w-lg">{card}</div>
      </div>
    );
  }

  return createPortal(
    <Fragment>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[1px] transition-opacity duration-200 ease-out motion-reduce:transition-none",
          isVisible ? "opacity-100" : "opacity-0",
        )}
        onClick={close}
        aria-hidden
      />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="pointer-events-auto flex max-h-[90dvh] w-full max-w-lg">{card}</div>
      </div>
    </Fragment>,
    document.body,
  );
}

function Banner({ url, alt }: { url: string; alt: string }) {
  const { showImage, onError } = useImageFallback(url || null);
  if (!url || !showImage) return null;
  return (
    <div className="aspect-[16/9] w-full shrink-0 overflow-hidden bg-slate-100">
      <img src={url} alt={alt} onError={onError} className="h-full w-full object-cover" />
    </div>
  );
}

function EventSlide({ item, titleId }: { item: PopupContent; titleId: string }) {
  const when = item.event_at || item.starts_at;
  const time = when ? formatTime(when) : "";
  const { showImage } = useImageFallback(item.image_url || null);
  const hasImage = !!item.image_url && showImage;
  return (
    <div data-testid="event-slide">
      <Banner url={item.image_url} alt="" />
      <div className={cn("px-5 pb-5", hasImage ? "pt-4" : "pt-5")}>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-primary-800">
          <Icon name="calendar" className="h-3.5 w-3.5" />
          Upcoming event
        </span>
        <h2 id={titleId} className="mt-3 break-words pr-8 text-lg font-semibold leading-snug text-slate-900 line-clamp-3">
          {item.title}
        </h2>
        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-600 line-clamp-4">{item.body}</p>
        <dl className="mt-4 grid gap-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
          {when && (
            <div className="flex items-center gap-2.5">
              <dt className="sr-only">Date</dt>
              <Icon name="calendar" className="h-4 w-4 shrink-0 text-primary-600" />
              <dd>{formatDay(when)}</dd>
            </div>
          )}
          {time && (
            <div className="flex items-center gap-2.5">
              <dt className="sr-only">Time</dt>
              <Icon name="clock" className="h-4 w-4 shrink-0 text-primary-600" />
              <dd>{time}</dd>
            </div>
          )}
          {item.location && (
            <div className="flex items-start gap-2.5">
              <dt className="sr-only">Location</dt>
              <Icon name="map-pin" className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
              <dd className="min-w-0 break-words">{item.location}</dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}

const PRIORITY_LABEL: Record<PopupContent["priority"], string> = { high: "High priority", medium: "Medium priority", low: "Low priority" };
const PRIORITY_TONE: Record<PopupContent["priority"], string> = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-slate-100 text-slate-700",
};

function AnnouncementSlide({ item, titleId }: { item: PopupContent; titleId: string }) {
  const isHigh = item.priority === "high";
  return (
    <div data-testid="announcement-slide">
      <div className={cn("flex items-center gap-2 px-5 py-3 pr-14", isHigh ? "bg-red-50" : "bg-primary-50")}>
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            isHigh ? "bg-red-100 text-red-700" : "bg-primary-100 text-primary-700",
          )}
        >
          <Icon name={isHigh ? "alert-triangle" : "bell"} className="h-4 w-4" />
        </span>
        <span className={cn("text-xs font-semibold uppercase tracking-wide", isHigh ? "text-red-700" : "text-primary-800")}>
          {isHigh ? "Important announcement" : "Announcement"}
        </span>
      </div>
      <Banner url={item.image_url} alt="" />
      <div className="px-5 pb-5 pt-4">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium", PRIORITY_TONE[item.priority])} data-testid="announcement-priority">
          {PRIORITY_LABEL[item.priority]}
        </span>
        <h2 id={titleId} className="mt-2 break-words text-lg font-semibold leading-snug text-slate-900 line-clamp-3">
          {item.title}
        </h2>
        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">{item.body}</p>
        {item.published_at && <p className="mt-4 text-xs text-slate-500">Published {formatDay(item.published_at)}</p>}
      </div>
    </div>
  );
}
